import { useMemo } from 'react';
import ContentComposer, { ComponentProps as ContentComposerProps } from '@/components/Internals/ContentComposer';
import WrapperContent from '@/components/Layout/WrapperContent';
import classNames from '@/utils/className';

export interface CompanyHeroProps {
  content?: ContentComposerProps[] | string;
  companyName: string;
  companySlug: string;
  scrollMarginTop: number;
  sectionId?: string;
  backgroundImageUrl?: string;
  ctaHref?: string;
  ctaLabel?: string;
  fallbackParagraphs?: string[];
  heading?: string;
}

const DEFAULT_FALLBACK_PARAGRAPHS = [
  'Esta empresa desenvolve produtos e serviços que impactam o dia a dia de milhões de pessoas ao redor do mundo.',
  'A inovação constante e o foco na experiência do usuário fazem parte do DNA da companhia.',
];

export default function CompanyHero({
  content,
  companyName,
  companySlug,
  scrollMarginTop,
  sectionId = companySlug,
  backgroundImageUrl = 'https://t.ctcdn.com.br/WevcqeQI5YN4s6lIbCRafr8WOjE=/i1068817.jpeg',
  ctaHref,
  ctaLabel = 'Saiba mais',
  fallbackParagraphs = DEFAULT_FALLBACK_PARAGRAPHS,
  heading,
}: CompanyHeroProps) {
  const normalizedHeading = heading || `Sobre a ${companyName}`;
  const heroCtaHref = ctaHref || `/empresa/${companySlug}/produtos/`;

  const paragraphs = useMemo(() => {
    if (!content) {
      return fallbackParagraphs.map((paragraph, index) => (
        <p key={`fallback-${index}`} className="text-base leading-relaxed text-mglGray900/80">
          {paragraph}
        </p>
      ));
    }

    if (typeof content === 'string') {
      return content
        .split('\n')
        .map(item => item.trim())
        .filter(Boolean)
        .slice(0, 2)
        .map((paragraph, index) => (
          <p key={`string-content-${index}`} className="text-base leading-relaxed text-mglGray900/80">
            {paragraph}
          </p>
        ));
    }

    const normalizedParagraphs = content.filter(item => item?.tipo !== 'html').slice(0, 2);

    if (normalizedParagraphs.length === 0) {
      return fallbackParagraphs.map((paragraph, index) => (
        <p key={`fallback-${index}`} className="text-base leading-relaxed text-mglGray900/80">
          {paragraph}
        </p>
      ));
    }

    return normalizedParagraphs.map((item, index) => (
      <ContentComposer {...item} key={`content-${item.tipo}-${index}`} className={classNames('text-base leading-relaxed text-mglGray900/80', item.className)} />
    ));
  }, [content, fallbackParagraphs]);

  return (
    <section id={sectionId} aria-labelledby={`${sectionId}-title`} className="relative isolate w-full overflow-hidden" style={{ scrollMarginTop }}>
      <div
        className="absolute inset-0 bg-white bg-cover bg-left bg-no-repeat desktop:bg-[size:120%]"
        style={{ backgroundImage: `url('${backgroundImageUrl}')` }}
        aria-hidden
      />
      <div className="absolute inset-0 bg-gradient-to-r from-white via-white/20 to-transparent" aria-hidden />

      <WrapperContent isOnHeader backgroundColor="transparent">
        <div className="relative z-10 flex min-h-[650px] w-full flex-col justify-center gap-6 px-6 py-10 text-mglGray900 phone:px-8 tablet:max-w-[540px] tablet:px-12 desktop:max-w-[620px] desktop:px-16">
          <h1 id={`${sectionId}-title`} className="text-3xl font-bold leading-tight text-mglGray900 tablet:text-4xl">
            {normalizedHeading}
          </h1>
          <div className="flex flex-col gap-4 font-thin">{paragraphs}</div>
          <a
            href={heroCtaHref}
            className="flex items-center justify-center self-start rounded-full border border-b-2 bg-mglBlue400 px-16 py-4 text-white shadow-[0_4px_0_0_#ffffff] transition-colors duration-200 hover:bg-mglBlue300 phone:justify-center">
            {ctaLabel}
          </a>
        </div>
      </WrapperContent>
    </section>
  );
}
